// src/components/CallShieldToggle.tsx
// ──────────────────────────────────────────────────────────────
// One-tap arm/disarm for Call Shield on the case detail page.
// Pulls physician profile + case data automatically.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "../lib/supabase";

// ── Types ────────────────────────────────────────────────────

interface ShieldState {
  status:
    | "idle"
    | "configuring"
    | "arming"
    | "armed"
    | "active"
    | "disarming"
    | "error";
  shieldId: string | null;
  phoneNumber: string | null;
  expiresAt: string | null;
  verificationEnabled: boolean;
  error: string | null;
}

type TimeMode = "duration" | "range";

interface TimeConfig {
  mode: TimeMode;
  durationMinutes: number;
  rangeStart: string;
  rangeEnd: string;
  timezone: string;
}

// ── Timezone helpers ─────────────────────────────────────────

const COMMON_TIMEZONES = [
  { value: "America/New_York", label: "Eastern", abbr: "ET" },
  { value: "America/Chicago", label: "Central", abbr: "CT" },
  { value: "America/Denver", label: "Mountain", abbr: "MT" },
  { value: "America/Los_Angeles", label: "Pacific", abbr: "PT" },
  { value: "America/Anchorage", label: "Alaska", abbr: "AKT" },
  { value: "Pacific/Honolulu", label: "Hawaii", abbr: "HT" },
];

function getLocalTimezone(): string {
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // If it matches a common one, use it; otherwise default to Eastern
  const match = COMMON_TIMEZONES.find((tz) => tz.value === local);
  return match ? local : "America/New_York";
}

function getTimezoneAbbr(tz: string): string {
  return COMMON_TIMEZONES.find((t) => t.value === tz)?.abbr || "ET";
}

interface CallShieldToggleProps {
  caseId: string;
  compact?: boolean;
}

// ── Duration presets ─────────────────────────────────────────

const DURATION_PRESETS = [
  { label: "30 min", minutes: 30 },
  { label: "1 hr", minutes: 60 },
  { label: "2 hr", minutes: 120 },
  { label: "4 hr", minutes: 240 },
  { label: "8 hr", minutes: 480 },
];

// ── Icons ────────────────────────────────────────────────────

function ShieldIcon({
  armed,
  className,
}: {
  armed: boolean;
  className?: string;
}) {
  // AB2 proportions — wider/stockier shield (w:h ≈ 0.85)
  return (
    <svg className={className} viewBox="0 0 24 28" fill="none">
      {armed ? (
        <>
          {/* Outer shield — filled */}
          <path
            d="M12 0 L24 3.8 L24 14.5 C24 21.1 18 25.5 12 28 C6 25.5 0 21.1 0 14.5 L0 3.8 Z"
            fill="currentColor"
          />
          {/* Inner shield — lighter overlay */}
          <path
            d="M12 1.5 L23 4.9 L23 14.2 C23 20.4 17.5 24.4 12 26.8 C6.5 24.4 1 20.4 1 14.2 L1 4.9 Z"
            fill="white"
            opacity="0.2"
          />
          {/* Checkmark */}
          <path
            d="M8.3 13.8 L10.8 16.6 L16.3 10.4"
            stroke="white"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      ) : (
        <>
          {/* Outline shield */}
          <path
            d="M12 1 L23 4.5 L23 14.3 C23 20.5 17.5 24.8 12 27.2 C6.5 24.8 1 20.5 1 14.3 L1 4.5 Z"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
      />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        opacity={0.25}
      />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1")
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
}

function timeUntil(isoDate: string): {
  label: string;
  expired: boolean;
  urgency: "normal" | "warning" | "critical";
} {
  const diff = new Date(isoDate).getTime() - Date.now();
  if (diff <= 0)
    return { label: "Expired", expired: true, urgency: "critical" };
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const urgency =
    diff < 600000 ? "critical" : diff < 1800000 ? "warning" : "normal";
  if (hours > 0)
    return { label: `${hours}h ${mins}m remaining`, expired: false, urgency };
  return { label: `${mins}m remaining`, expired: false, urgency };
}

function getDefaultRangeStart(): string {
  const now = new Date();
  const mins = now.getMinutes();
  if (mins < 30) {
    now.setMinutes(30, 0, 0);
  } else {
    now.setHours(now.getHours() + 1, 0, 0, 0);
  }
  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`;
}

function getDefaultRangeEnd(start: string): string {
  const [h, m] = start.split(":").map(Number);
  const endH = Math.min(h + 2, 23);
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function format12h(time24: string): string {
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function calcWindowHours(config: TimeConfig): number {
  if (config.mode === "duration") return config.durationMinutes / 60;

  // Build a Date in the selected timezone for rangeEnd
  const nowLocal = new Date();
  const [endH, endM] = config.rangeEnd.split(":").map(Number);

  // Create a date string in the target timezone to calculate offset
  const todayStr = nowLocal.toLocaleDateString("en-CA", {
    timeZone: config.timezone,
  }); // YYYY-MM-DD
  const endDateStr = `${todayStr}T${String(endH).padStart(2, "0")}:${String(
    endM
  ).padStart(2, "0")}:00`;

  // Get the UTC equivalent of the target time in the selected timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: config.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  // Use a simpler approach: compute offset between local and target timezone
  const end = new Date(endDateStr);
  const localOffset = end.getTimezoneOffset();
  const targetNow = new Date(
    nowLocal.toLocaleString("en-US", { timeZone: config.timezone })
  );
  const targetOffset =
    Math.round((targetNow.getTime() - nowLocal.getTime()) / 60000) +
    localOffset;

  // Adjust end time by the timezone difference
  const adjustedEnd = new Date(end.getTime() - targetOffset * 60000);
  if (adjustedEnd.getTime() <= nowLocal.getTime())
    adjustedEnd.setDate(adjustedEnd.getDate() + 1);

  return Math.max(0.25, (adjustedEnd.getTime() - nowLocal.getTime()) / 3600000);
}

// ── Component ────────────────────────────────────────────────

export default function CallShieldToggle({
  caseId,
  compact = false,
}: CallShieldToggleProps) {
  const { user } = useUser();
  const [state, setState] = useState<ShieldState>({
    status: "idle",
    shieldId: null,
    phoneNumber: null,
    expiresAt: null,
    verificationEnabled: false,
    error: null,
  });
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState<{
    label: string;
    expired: boolean;
    urgency: string;
  }>({
    label: "",
    expired: false,
    urgency: "normal",
  });
  const [timeConfig, setTimeConfig] = useState<TimeConfig>(() => {
    const rangeStart = getDefaultRangeStart();
    return {
      mode: "duration",
      durationMinutes: 120,
      rangeStart,
      rangeEnd: getDefaultRangeEnd(rangeStart),
      timezone: getLocalTimezone(),
    };
  });

  const userId = user?.id;

  // ── Check for existing armed shield on mount ──────────────
  useEffect(() => {
    if (!userId || !caseId) return;
    const checkExisting = async () => {
      const { data: shield } = await supabase
        .from("call_shields")
        .select("id, status, expires_at, verification_map, number_id")
        .eq("case_id", caseId)
        .eq("user_id", userId)
        .in("status", ["armed", "active"])
        .order("armed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (shield) {
        const { data: numberRecord } = await supabase
          .from("call_shield_numbers")
          .select("twilio_number")
          .eq("id", shield.number_id)
          .single();
        setState({
          status: shield.status as "armed" | "active",
          shieldId: shield.id,
          phoneNumber: numberRecord?.twilio_number || null,
          expiresAt: shield.expires_at,
          verificationEnabled: !!shield.verification_map,
          error: null,
        });
      }
    };
    checkExisting();
  }, [userId, caseId]);

  // ── Countdown timer ───────────────────────────────────────
  useEffect(() => {
    if (!state.expiresAt || !["armed", "active"].includes(state.status)) return;
    const tick = () => setCountdown(timeUntil(state.expiresAt!));
    tick();
    const interval = setInterval(tick, 15000);
    return () => clearInterval(interval);
  }, [state.expiresAt, state.status]);

  const openConfig = useCallback(() => {
    const rangeStart = getDefaultRangeStart();
    setTimeConfig((prev) => ({
      ...prev,
      rangeStart,
      rangeEnd: getDefaultRangeEnd(rangeStart),
    }));
    setState((s) => ({ ...s, status: "configuring", error: null }));
  }, []);

  const cancelConfig = useCallback(() => {
    setState((s) => ({ ...s, status: "idle", error: null }));
  }, []);

  // ── Arm ────────────────────────────────────────────────────
  const arm = useCallback(async () => {
    if (!userId || !caseId) return;
    setState((s) => ({ ...s, status: "arming", error: null }));
    try {
      const { data: profile } = await supabase
        .from("physician_profiles")
        .select("physician_name, npi_number, practice_name, phone_number")
        .eq("user_id", userId)
        .maybeSingle();
      const windowHours = calcWindowHours(timeConfig);
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-shield-arm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: "arm",
            case_id: caseId,
            user_id: userId,
            display_name: profile?.practice_name
              ? `${profile.practice_name}`
              : profile?.physician_name
              ? `${profile.physician_name}'s office`
              : "Doctor's office",
            physician_name: profile?.physician_name || undefined,
            physician_npi: profile?.npi_number || undefined,
            practice_name: profile?.practice_name || undefined,
            physician_phone: profile?.phone_number || undefined,
            window_hours: windowHours,
          }),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to arm shield");
      setState({
        status: "armed",
        shieldId: data.shield_id,
        phoneNumber: data.phone_number,
        expiresAt: data.expires_at,
        verificationEnabled: data.verification_enabled || false,
        error: null,
      });
    } catch (e: any) {
      setState((s) => ({
        ...s,
        status: "error",
        error: e.message || "Failed to arm",
      }));
    }
  }, [userId, caseId, timeConfig]);

  // ── Disarm ─────────────────────────────────────────────────
  const disarm = useCallback(async () => {
    if (!userId || !caseId) return;
    setState((s) => ({ ...s, status: "disarming" }));
    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-shield-arm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: "disarm",
            case_id: caseId,
            user_id: userId,
          }),
        }
      );
      setState({
        status: "idle",
        shieldId: null,
        phoneNumber: null,
        expiresAt: null,
        verificationEnabled: false,
        error: null,
      });
    } catch {
      setState((s) => ({ ...s, status: "armed" }));
    }
  }, [userId, caseId]);

  const copyNumber = useCallback(() => {
    if (state.phoneNumber) {
      navigator.clipboard.writeText(state.phoneNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [state.phoneNumber]);

  // ── Derived state ─────────────────────────────────────────
  const isArmed = state.status === "armed" || state.status === "active";
  const isLoading = state.status === "arming" || state.status === "disarming";
  const isExpired = isArmed && countdown.expired;
  const isArmedActive = isArmed && !countdown.expired;

  // ── Compact mode ──────────────────────────────────────────
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {isArmed && state.phoneNumber && (
          <button
            onClick={copyNumber}
            className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            title="Copy Call Shield number"
          >
            <PhoneIcon className="w-3.5 h-3.5" />
            <span className="font-mono">{formatPhone(state.phoneNumber)}</span>
            {copied && (
              <span className="text-green-400 text-[10px]">copied</span>
            )}
          </button>
        )}
        <button
          onClick={isArmed ? disarm : openConfig}
          disabled={isLoading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200
            ${
              isArmed
                ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                : "bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-200 border border-slate-600/30"
            }
            ${isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
        >
          {isLoading ? (
            <LoadingSpinner className="w-3.5 h-3.5" />
          ) : (
            <ShieldIcon armed={isArmed} className="w-3.5 h-3.5" />
          )}
          {isArmed ? "Shield On" : "Arm Shield"}
        </button>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // FULL CARD STATES
  // ════════════════════════════════════════════════════════════

  // ── ARMED & ACTIVE ────────────────────────────────────────
  if (isArmedActive) {
    return (
      <div className="rounded-xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 via-white to-purple-50/50 shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-purple-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
            </span>
            <span className="text-xs font-semibold text-white tracking-wide uppercase">
              Shield Active
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <ClockIcon className="w-3.5 h-3.5 text-purple-200" />
            <span
              className={`text-xs font-medium ${
                countdown.urgency === "critical"
                  ? "text-red-300"
                  : countdown.urgency === "warning"
                  ? "text-amber-300"
                  : "text-purple-200"
              }`}
            >
              {countdown.label}
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldIcon armed className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-semibold text-slate-800">
              Call Shield
            </span>
            {state.verificationEnabled && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                <CheckIcon className="w-2.5 h-2.5" /> AI Verified
              </span>
            )}
          </div>
          {state.phoneNumber && (
            <button
              onClick={copyNumber}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all group"
              title="Copy to clipboard"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <PhoneIcon className="w-4 h-4 text-purple-600" />
                </div>
                <span className="font-mono text-base font-semibold text-slate-800 tracking-tight">
                  {formatPhone(state.phoneNumber)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {copied ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                    <CheckIcon className="w-3 h-3" /> Copied
                  </span>
                ) : (
                  <CopyIcon className="w-4 h-4 text-slate-400 group-hover:text-purple-500 transition-colors" />
                )}
              </div>
            </button>
          )}
          <p className="text-[11px] text-slate-400 mt-2 pl-0.5">
            Give this number to the insurer. You'll be notified when the
            reviewer calls.
          </p>
          <button
            onClick={disarm}
            disabled={isLoading}
            className={`mt-3 w-full py-2 rounded-lg text-xs font-medium transition-all duration-200 text-slate-500 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 ${
              isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"
            }`}
          >
            {state.status === "disarming" ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner className="w-3.5 h-3.5" /> Disarming...
              </span>
            ) : (
              "Disarm Shield"
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── EXPIRED ───────────────────────────────────────────────
  if (isExpired) {
    return (
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-slate-400 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex rounded-full h-2 w-2 bg-slate-300" />
            <span className="text-xs font-semibold text-white tracking-wide uppercase">
              Shield Expired
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldIcon armed={false} className="w-5 h-5 text-slate-400" />
            <span className="text-sm font-semibold text-slate-500">
              Call Shield
            </span>
            {state.verificationEnabled && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 border border-slate-200">
                <CheckIcon className="w-2.5 h-2.5" /> AI Verified
              </span>
            )}
          </div>
          {state.phoneNumber && (
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <PhoneIcon className="w-4 h-4 text-slate-400" />
              </div>
              <span className="font-mono text-base font-semibold text-slate-400 tracking-tight">
                {formatPhone(state.phoneNumber)}
              </span>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={openConfig}
              disabled={isLoading}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow-md hover:shadow-purple-200 ${
                isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"
              }`}
            >
              Re-arm Shield
            </button>
            <button
              onClick={disarm}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 bg-white ${
                isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"
              }`}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CONFIGURING — time picker ─────────────────────────────
  if (state.status === "configuring") {
    const summaryText =
      timeConfig.mode === "duration"
        ? `Active for ${
            timeConfig.durationMinutes >= 60
              ? `${timeConfig.durationMinutes / 60} hour${
                  timeConfig.durationMinutes > 60 ? "s" : ""
                }`
              : `${timeConfig.durationMinutes} minutes`
          }`
        : `Active ${format12h(timeConfig.rangeStart)} – ${format12h(
            timeConfig.rangeEnd
          )} ${getTimezoneAbbr(timeConfig.timezone)}`;

    return (
      <div className="rounded-xl border-2 border-purple-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldIcon armed={false} className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-semibold text-slate-800">
              Arm Call Shield
            </span>
          </div>
          <button
            onClick={cancelConfig}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Cancel
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Mode toggle */}
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-2">
              How long should the shield stay active?
            </label>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                onClick={() =>
                  setTimeConfig((c) => ({ ...c, mode: "duration" }))
                }
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-all ${
                  timeConfig.mode === "duration"
                    ? "bg-purple-600 text-white"
                    : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                <ClockIcon className="w-3.5 h-3.5" /> Duration
              </button>
              <button
                onClick={() => setTimeConfig((c) => ({ ...c, mode: "range" }))}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-all ${
                  timeConfig.mode === "range"
                    ? "bg-purple-600 text-white"
                    : "bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                <CalendarIcon className="w-3.5 h-3.5" /> Time Range
              </button>
            </div>
          </div>

          {/* Duration presets */}
          {timeConfig.mode === "duration" && (
            <div className="flex flex-wrap gap-2">
              {DURATION_PRESETS.map((preset) => (
                <button
                  key={preset.minutes}
                  onClick={() =>
                    setTimeConfig((c) => ({
                      ...c,
                      durationMinutes: preset.minutes,
                    }))
                  }
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all
                    ${
                      timeConfig.durationMinutes === preset.minutes
                        ? "bg-purple-100 text-purple-700 border-2 border-purple-300 shadow-sm"
                        : "bg-slate-50 text-slate-600 border border-slate-200 hover:border-purple-200 hover:bg-purple-50"
                    }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          {/* Time range pickers */}
          {timeConfig.mode === "range" && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-slate-400 block mb-1">
                    From
                  </label>
                  <input
                    type="time"
                    value={timeConfig.rangeStart}
                    onChange={(e) =>
                      setTimeConfig((c) => ({
                        ...c,
                        rangeStart: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 font-medium focus:outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100 transition-all"
                  />
                </div>
                <span className="text-slate-300 mt-4 font-medium">–</span>
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-slate-400 block mb-1">
                    Until
                  </label>
                  <input
                    type="time"
                    value={timeConfig.rangeEnd}
                    onChange={(e) =>
                      setTimeConfig((c) => ({ ...c, rangeEnd: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 font-medium focus:outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100 transition-all"
                  />
                </div>
              </div>
              {/* Timezone selector */}
              <div>
                <label className="text-[10px] font-medium text-slate-400 block mb-1">
                  Timezone
                </label>
                <select
                  value={timeConfig.timezone}
                  onChange={(e) =>
                    setTimeConfig((c) => ({ ...c, timezone: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 font-medium bg-white focus:outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                  }}
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.abbr})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Summary + Arm */}
          <div>
            <p className="text-[11px] text-slate-400 mb-3 text-center">
              {summaryText}
            </p>
            <button
              onClick={arm}
              disabled={isLoading}
              className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow-md hover:shadow-purple-200 ${
                isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"
              }`}
            >
              {state.status === "arming" ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner className="w-4 h-4" /> Arming...
                </span>
              ) : (
                "Arm Shield"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── IDLE / DISARMED ───────────────────────────────────────
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center flex-shrink-0">
              <ShieldIcon armed={false} className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <span className="text-sm font-semibold text-slate-800 block">
                Call Shield
              </span>
              <span className="text-[11px] text-slate-400 leading-tight">
                Auto-answers and holds the reviewer for you
              </span>
            </div>
          </div>
          <button
            onClick={openConfig}
            disabled={isLoading}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow-md hover:shadow-purple-200 ${
              isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"
            }`}
          >
            Arm Shield
          </button>
        </div>
        {state.error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
            <p className="text-xs text-red-600">{state.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
